import React from 'react';
import PropTypes from 'prop-types';
import { Grid, Typography } from '@material-ui/core';
import Autocomplete from '../../../../common/Inputs/Autocomplete';
import CheckBox from '../../../../common/Inputs/CheckBox';
import { US_REGIONS } from '../../../../../constants/regions';
import { useTrans } from '../../../../../services/i18n';
import { normalizeRegionIds } from '../../../../../utils/regionUtils';

const identityLabel = (label) => label || '';

const normalizeSelectedRegions = (next) => {
  if (Array.isArray(next)) return next;
  if (next) return [next];
  return [];
};

/**
 * Multi-select Region control for Location filter modals.
 * Controlled by region id strings; closes on each selection (disableCloseOnSelect=false).
 */
const RegionSelector = ({ value = [], onChange, helperText, limitedHelperText, showLimitedHint = false }) => {
  const { trans } = useTrans();

  const selectedRegions = React.useMemo(() => {
    const ids = new Set(normalizeRegionIds(value));
    return US_REGIONS.filter((region) => ids.has(region.id));
  }, [value]);

  const handleChange = (event) => {
    const regions = normalizeSelectedRegions(event?.target?.value);
    onChange?.(regions.map((region) => region.id).filter(Boolean));
  };

  const showHelpers = Boolean(helperText || (showLimitedHint && limitedHelperText));

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Autocomplete
          name="region_data"
          label={trans('region-optional')}
          placeholder={trans('select-regions')}
          options={US_REGIONS}
          labelKey="label"
          value={selectedRegions}
          onChange={handleChange}
          multiple
          disableCloseOnSelect={false}
          formatLabelCB={identityLabel}
          getOptionSelected={(option, selected) => option?.id === selected?.id}
          renderOption={(option, { selected }) => (
            <React.Fragment>
              <CheckBox checked={selected} />
              {option.label}
            </React.Fragment>
          )}
          PopperProps={{
            disablePortal: false,
            style: { zIndex: 1500 },
          }}
        />
      </Grid>
      {showHelpers && (
        <Grid item xs={12}>
          {helperText && (
            <Typography variant="body2" color="textSecondary">
              {helperText}
            </Typography>
          )}
          {showLimitedHint && limitedHelperText && (
            <Typography variant="body2" color="textSecondary">
              {limitedHelperText}
            </Typography>
          )}
        </Grid>
      )}
    </Grid>
  );
};

RegionSelector.propTypes = {
  value: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])),
  onChange: PropTypes.func,
  helperText: PropTypes.string,
  limitedHelperText: PropTypes.string,
  showLimitedHint: PropTypes.bool,
};

export default RegionSelector;
